import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { useStudyStore } from './study';
import type { SidebarLinkModel } from '@/components/sidebar/interfaces/SidebarLinkModel';
import { SidebarLinkIdentifiers } from '@/components/sidebar/LinkIdentifiers';
import { LocalStorageUtils, routeConstants } from '@/utils';
import { useIsMobile } from '@/composables/breakpoints';

// Exported so the top bar's brand zone can span exactly the sidebar column.
export const SIDEBAR_EXPANDED_WIDTH = 260;
export const SIDEBAR_COLLAPSED_WIDTH = 64;

// The mobile drawer's panel and the button that opens it, which live in different components
// (`DashboardContainer` and `TopBar`). Shared from here so the two can point at each other for
// assistive technology, and so the container can hand focus back across that boundary.
export const MOBILE_NAV_PANEL_ID = 'mobile-nav-drawer';
export const MOBILE_NAV_TOGGLE_ID = 'mobile-nav-toggle';

export const useDashboardStore = defineStore('dashboard', () => {
  const studyStore = useStudyStore();

  const selectedMenu = ref<number>(0);
  const sidebarLinks = ref<SidebarLinkModel[]>([]);
  const userOptionLinks = ref<SidebarLinkModel[]>([]);

  const belowMobile = useIsMobile();

  /** What the user chose on a desktop, remembered across sessions. */
  const collapsedPreference = ref<boolean>(LocalStorageUtils.getSidebarCollapsed());

  /**
   * Whether the sidebar draws as the 64px icon rail.
   *
   * The preference, except on a phone — there the sidebar is not a column at all but a drawer that
   * slides over the content, and a drawer the width of an icon rail is no use to anyone. Held here
   * rather than passed down because three components read it independently (`Sidebar`,
   * `SidebarLink`, `StudySelector`, the last of which also flies its popup out sideways when the
   * rail is too narrow for a dropdown), and threading an override through all of them would leave
   * four places that could disagree about one layout.
   */
  const sidebarCollapsed = computed(() => !belowMobile.value && collapsedPreference.value);

  const sidebarWidth = computed(() =>
    `${sidebarCollapsed.value ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_EXPANDED_WIDTH}px`
  );

  const toggleSidebar = () => {
    collapsedPreference.value = !collapsedPreference.value;
    LocalStorageUtils.setSidebarCollapsed(collapsedPreference.value);
  };

  /**
   * Whether the mobile navigation drawer is open.
   *
   * Session state, not a preference: a drawer that slides over the content is a thing you open to
   * get somewhere, so it closes on arrival (`DashboardContainer` watches the route) and is never
   * restored. It is also never open above `$bp-mobile`, where the sidebar is a column instead.
   */
  const mobileNavOpen = ref<boolean>(false);

  const toggleMobileNav = () => {
    mobileNavOpen.value = !mobileNavOpen.value;
  };

  const closeMobileNav = () => {
    mobileNavOpen.value = false;
  };

  const setMenu = (id: number) => {
    selectedMenu.value = id;
  };

  const setDashboardLinks = () => {
    const permissions = studyStore.studyPermissions;

    const links: SidebarLinkModel[] = [];

    const dataNestedLinks: SidebarLinkModel[] = [];

    if (permissions?.manageInvest && selectedMenu.value === SidebarLinkIdentifiers.DATA) {
      dataNestedLinks.push({
        id: SidebarLinkIdentifiers.NESTED_NEW_VIEW,
        label: 'New View',
        icon: 'plus-circle',
        iconStyle: 'fal',
        selected: false,
        route: '',
        routeEnabled: false
      });
    }

    links.push({
      id: SidebarLinkIdentifiers.DATA,
      label: 'Data',
      icon: 'database',
      iconStyle: 'fal',
      selected: false,
      route: routeConstants.DATA.name,
      routeEnabled: false,
      nestedLinks: dataNestedLinks
    });

    if (permissions?.manageInvest) {
      links.push({
        id: SidebarLinkIdentifiers.FORMS,
        label: 'Forms',
        icon: 'layer-group',
        iconStyle: 'fal',
        selected: false,
        route: routeConstants.FORMS.name,
        routeEnabled: true
      });

      links.push({
        id: SidebarLinkIdentifiers.ANALYTICS,
        label: 'Analytics',
        icon: 'chart-bar',
        iconStyle: 'fal',
        selected: false,
        route: routeConstants.ANALYTICS.name,
        routeEnabled: true
      });
    }

    // Its own permission, not `manageInvest` or `viewEntries`: spdata-api sets it from `VIEW_CASES`
    // on this study, the check `GET study/:id/child-studies` makes (ENG-8887).
    if (permissions?.viewChildStudies === true) {
      links.push({
        id: SidebarLinkIdentifiers.CHILD_STUDIES,
        label: 'Child studies',
        icon: 'sitemap',
        iconStyle: 'fal',
        selected: false,
        route: routeConstants.CHILD_STUDIES.name,
        routeEnabled: true
      });
    }

    if (studyStore.selectedStudy?.flags?.customDataDictionary === 'PUMP') {
      links.push({
        id: SidebarLinkIdentifiers.SURGEON_DASHBOARD,
        label: 'Surgeon Dashboard',
        icon: 'monitor-waveform',
        iconStyle: 'far',
        selected: false,
        route: routeConstants.DEFAULT_SURGEON_DASHBOARD.name,
        routeEnabled: true
      });

      if (studyStore.selectedStudy?.flags?.pumpQcReports?.enable) {
        links.push({
          id: SidebarLinkIdentifiers.PUMP_QC_REPORT,
          label: 'PUMP Reports',
          icon: 'file',
          iconStyle: 'fal',
          selected: false,
          route: routeConstants.PUMP_QC_REPORT.name,
          routeEnabled: true
        });
      }
    }

    if (permissions?.export) {
      links.push({
        id: SidebarLinkIdentifiers.EXPORTS,
        label: 'Data Exports',
        icon: 'download',
        iconStyle: 'fal',
        selected: false,
        route: routeConstants.EXPORT.name,
        routeEnabled: true
      });
    }

    if (permissions?.manageInvest) {
      links.push({
        id: SidebarLinkIdentifiers.ACTIVITY,
        label: 'Activity',
        icon: 'list',
        iconStyle: 'fal',
        selected: false,
        route: routeConstants.ACTIVITY.name,
        routeEnabled: true
      });

      links.push({
        id: SidebarLinkIdentifiers.MANAGE_USERS,
        label: 'Users',
        icon: 'users',
        iconStyle: 'fal',
        selected: false,
        route: routeConstants.MANAGE.name,
        routeEnabled: false
      });

      links.push({
        id: SidebarLinkIdentifiers.STUDY_SETTINGS,
        label: 'Study Settings',
        icon: 'cog',
        iconStyle: 'fal',
        selected: false,
        route: routeConstants.STUDY_SETTINGS.name,
        routeEnabled: true
      });
    }

    sidebarLinks.value = links;

    setUserOptionLinks();
  };

  const setSelectedDashboardLink = (id: number) => {
    sidebarLinks.value.forEach(link => link.selected = link.id === id);
  };

  const setUserOptionLinks = () => {
    const organizationIdentifier = studyStore.selectedStudy?.organizationIdentifier;
    const permissions = studyStore.studyPermissions;

    const userLinks: SidebarLinkModel[] = [];

    userLinks.push({
      id: SidebarLinkIdentifiers.ACCOUNT_SETTINGS,
      label: 'Account settings',
      icon: 'circle-user',
      iconStyle: 'fas',
      selected: false,
      route: routeConstants.ACCOUNT_SETTINGS.name,
      routeEnabled: true
    });

    if (permissions?.manageOrganization) {
      userLinks.push({
        id: SidebarLinkIdentifiers.BILLING_SETTINGS,
        label: 'Billing settings',
        icon: 'receipt',
        iconStyle: 'fas',
        selected: false,
        route: `organizations/${organizationIdentifier}/billing`,
        routeEnabled: false
      });
    }

    userLinks.push({
      id: SidebarLinkIdentifiers.LOG_OUT,
      label: 'Log out',
      icon: 'sign-out',
      iconStyle: 'fas',
      selected: false,
      route: 'logout',
      routeEnabled: true
    });

    userOptionLinks.value = userLinks;
  };

  return {
    selectedMenu,
    sidebarLinks,
    userOptionLinks,
    sidebarCollapsed,
    sidebarWidth,
    toggleSidebar,
    mobileNavOpen,
    toggleMobileNav,
    closeMobileNav,
    setMenu,
    setDashboardLinks,
    setUserOptionLinks,
    setSelectedDashboardLink
  };
});
